import { cloneDeep } from 'lodash';
import {
    INIT_EDITED_GUIDE,
    NOTIFY_SPOT_COLLECTION_EDITOR_SHOWN,
    PATCH_GUIDE_FIELD,
    SET_CURRENT_GUIDE_GOOD_TO_KNOW_ITEMS,
    SET_CURRENT_GUIDE_HIGHLIGHTS,
    SET_CURRENT_GUIDE_NUMBER_OF_DAYS_ITEMS,
    SET_CURRENT_GUIDE_STORIES,
    START_ADDING_GUIDE,
    STOP_ADDING_GUIDE,
    STOP_EDITING_GUIDE,
} from './action-types';

import { AnyAction, Dispatch } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { getCurrentUser } from 'store/auth/selectors';
import { loadGoodToKnowItemsForGuide } from 'store/good-to-know-items/actions';
import { loadGuide } from 'store/guides/actions';
import { loadHighlightsForGuide } from 'store/highlight-items/actions';
import { loadNumberOfDaysItemsForGuide } from 'store/number-of-days-items/actions';
import { loadStoriesForGuide } from 'store/story-items/actions';
import { getPolygonGeometryFromBBox } from 'utils/geojson-utils';

const createReadOnlyThunk = <T>() => {
    return async (): Promise<T> => undefined as T;
};

/**
 * Starts the edition of a new guide
 */
export const startAddingGuide = () => {
    return async (dispatch: Dispatch, getState: () => RootState) => {
        const state = getState();
        const currentUser = getCurrentUser(state);

        dispatch({
            type: START_ADDING_GUIDE,
            author: currentUser,
        });
    };
};

/**
 * Stops the edition of a new guide
 */
export const stopAddingGuide = () => {
    return {
        type: STOP_ADDING_GUIDE,
    };
};

/**
 * Starts the edition of an existing guide
 */
export const startEditingGuide = (guideId: Id) => {
    return (dispatch: ThunkDispatch<RootState, unknown, AnyAction>) => {
        dispatch(loadGuide(guideId)).then((guide) => {
            dispatch({
                type: INIT_EDITED_GUIDE,
                guide,
            });
        });
    };
};

/**
 * Stops the edition of an existing guide
 */
export const stopEditingGuide = () => {
    return {
        type: STOP_EDITING_GUIDE,
    };
};

/**
 * Changes the value of a field in the currently edited guided
 */
export const patchGuideField = (fieldName: string, newValue: unknown) => {
    return {
        type: PATCH_GUIDE_FIELD,
        fieldName,
        newValue,
    };
};

/**
 * Changes the location in the currently edited guided, updating its viewport fields if there is BoundingBox info
 */
export const patchGuideLocation = (newLocation: PSLocationWithBB) => {
    return (dispatch: ThunkDispatch<RootState, unknown, AnyAction>) => {
        dispatch(patchGuideField('location', newLocation));
        if (newLocation.boundingbox) {
            const polygon = getPolygonGeometryFromBBox(newLocation.boundingbox);
            dispatch(patchGuideField('viewport_area', polygon));
            dispatch(patchGuideField('search_area', polygon));
        }
    };
};

/**
 * Saves a new guide (already should be in the state) in the DB
 */
export const saveNewGuide = (_newCover: CMSMedia) => {
    return createReadOnlyThunk<void>();
};

/**
 * Patches the guide being edited
 */
export const patchGuide = (
    _newCover: CMSMedia
): ((dispatch: Dispatch, getState: () => RootState) => Promise<Guide>) => {
    return createReadOnlyThunk<Guide>();
};

/**
 * Patches only one field of the guide being edited
 */
export const patchGuideSingleField = (_fieldName: keyof Guide, _value: unknown) => {
    return createReadOnlyThunk<Guide>();
};

/**
 * Notifies that the spot collection editor is shown or not
 */
export const notifySpotCollectionEditorShown = (isShown: boolean) => {
    return {
        type: NOTIFY_SPOT_COLLECTION_EDITOR_SHOWN,
        isShown,
    };
};

/**
 * Starts the edition of an existing guide
 */
export const initGuideStories = (guideId: Id) => {
    return async (dispatch: ThunkDispatch<RootState, unknown, AnyAction>) => {
        const originalStories = (await dispatch(loadStoriesForGuide(guideId))) ?? [];

        dispatch({
            type: SET_CURRENT_GUIDE_STORIES,
            currentStories: fixOrder(cloneDeep(originalStories)),
        });
    };
};

export const initGuideHighlights = (guideId: Id) => {
    return async (dispatch: ThunkDispatch<RootState, unknown, AnyAction>) => {
        const originalHighlights = (await dispatch(loadHighlightsForGuide(guideId))) ?? [];

        dispatch({
            type: SET_CURRENT_GUIDE_HIGHLIGHTS,
            currentHighlights: fixOrder(cloneDeep(originalHighlights)),
        });
    };
};

export const initGuideGoodToKnowItems = (guideId: Id) => {
    return async (dispatch: ThunkDispatch<RootState, unknown, AnyAction>) => {
        const originalItems = (await dispatch(loadGoodToKnowItemsForGuide(guideId))) ?? [];

        dispatch({
            type: SET_CURRENT_GUIDE_GOOD_TO_KNOW_ITEMS,
            currentGoodToKnowItems: fixOrder(cloneDeep(originalItems)),
        });
    };
};

export const initGuideNumberOfDaysItems = (guideId: Id) => {
    return async (dispatch: ThunkDispatch<RootState, unknown, AnyAction>) => {
        const originalItems = (await dispatch(loadNumberOfDaysItemsForGuide(guideId))) ?? [];

        dispatch({
            type: SET_CURRENT_GUIDE_NUMBER_OF_DAYS_ITEMS,
            currentNumberOfDaysItems: fixOrder(cloneDeep(originalItems)),
        });
    };
};

/**
 * Saves the current guide stories being edited
 *
 * If their cms_media has changed, it should be already uploaded and updated in this object,
 * only the reference to it will be updated in this action
 */
export const saveGuideStories = (_guideId: Id, _currentStories: StoryItem[]) => {
    return createReadOnlyThunk<void>();
};

/**
 * Saves the current guide highlights being edited
 *
 * If their cms_media has changed, it should be already uploaded and updated in this object,
 * only the reference to it will be updated in this action
 */
export const saveGuideHighlights = (_guideId: Id, _currentHighlights: HighlightItem[]) => {
    return createReadOnlyThunk<void>();
};

/**
 * Saves the current guide good to know items being edited
 */
export const saveGoodToKnowItems = (_guideId: Id, _currentGoodToKnowItems: GoodToKnowItem[]) => {
    return createReadOnlyThunk<void>();
};

/**
 * Saves the current guide number of days items being edited
 */
export const saveNumberOfDaysItems = (_guideId: Id, _currentNumberOfDaysItems: NumberOfDaysItem[]) => {
    return createReadOnlyThunk<void>();
};

/**
 * Resets the stories being edited for a guide
 */
export const resetGuideStories = () => {
    return {
        type: SET_CURRENT_GUIDE_STORIES,
        currentStories: null,
    };
};

/**
 * Resets the highlights being edited for a guide
 */
export const resetGuideHighlights = () => {
    return {
        type: SET_CURRENT_GUIDE_HIGHLIGHTS,
        currentHighlights: null,
    };
};

/**
 * Resets the good to know items being edited for a guide
 */
export const resetGuideGoodToKnowItems = () => {
    return {
        type: SET_CURRENT_GUIDE_GOOD_TO_KNOW_ITEMS,
        currentGoodToKnowItems: null,
    };
};

/**
 * Resets the number of days items being edited for a guide
 */
export const resetGuideNumberOfDaysItems = () => {
    return {
        type: SET_CURRENT_GUIDE_NUMBER_OF_DAYS_ITEMS,
        currentNumberOfDaysItems: null,
    };
};

/**
 * Notifies the change in guide stories being edited
 */
export const notifyGuideStoriesChange = (stories: StoryItem[]) => {
    return {
        type: SET_CURRENT_GUIDE_STORIES,
        currentStories: fixOrder(stories),
    };
};

/**
 * Notifies the change in guide highlights being edited
 */
export const notifyGuideHighlightsChange = (highlights: HighlightItem[]) => {
    return {
        type: SET_CURRENT_GUIDE_HIGHLIGHTS,
        currentHighlights: fixOrder(highlights),
    };
};

/**
 * Notifies the change in guide good to know items being edited
 */
export const notifyGuideGoodToKnowItemsChange = (goodToKnowItems: GoodToKnowItem[]) => {
    return {
        type: SET_CURRENT_GUIDE_GOOD_TO_KNOW_ITEMS,
        currentGoodToKnowItems: fixOrder(goodToKnowItems),
    };
};

/**
 * Notifies the change in guide number of days items being edited
 */
export const notifyGuideNumberOfDaysItemsChange = (numberOfDaysItems: NumberOfDaysItem[]) => {
    return {
        type: SET_CURRENT_GUIDE_NUMBER_OF_DAYS_ITEMS,
        currentNumberOfDaysItems: fixOrder(numberOfDaysItems),
    };
};

function fixOrder<T extends { order: number }>(items: T[]): T[] {
    const orderedItems = items.slice().sort((a, b) => a.order - b.order);

    return orderedItems.map((item, index) => {
        return {
            ...item,
            order: index,
        };
    });
}
